import { FunctionComponent, createContext, useContext, useRef, useState } from "react";
import Model from "../components/model";
import { Configurator } from "@rromikas/configurio";
import { useModel } from "hooks/useModel";
import ModelPartCustomizer from "./model-part-customizer";
import ModelPartsList from "./model-parts-list";
import CameraPositionField from "./position-field";
import { Vector3 } from "three";
import InitialSelectionsField from "./initial-selections-field";
import GroupsList from "./groups-list/groups-list";
import GroupCustomizer from "./groups-list/group-customizer";
import TakePhotoButton from "./take-photo-button";
import uniqid from "uniqid";
import Input from "../components/input";
import RotationField from "./rotation-field";

export interface EditorProps {
  configurator: Configurator;
  setConfigurator: React.Dispatch<React.SetStateAction<Configurator | undefined>>;
}

interface Selection {
  index: number;
  type: "group" | "part";
}

export const EditorContext = createContext<{
  model: ReturnType<typeof useModel> | undefined;
  hoveredListItemIndex: number | undefined;
  setHoveredListItemIndex: React.Dispatch<React.SetStateAction<number | undefined>>;
  selection: Selection | undefined;
  setSelection: React.Dispatch<React.SetStateAction<Selection | undefined>>;
  cameraPosition: Vector3 | undefined;
  setCameraPosition: React.Dispatch<React.SetStateAction<Vector3 | undefined>>;
  configurator: Configurator;
}>({} as any);

export const useEditor = () => useContext(EditorContext);

const Editor: FunctionComponent<EditorProps> = ({ configurator, setConfigurator }) => {
  const [model, setModel] = useState<ReturnType<typeof useModel>>();
  const [hoveredListItemIndex, setHoveredListItemIndex] = useState<number>();
  const [selection, setSelection] = useState<Selection>();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [cameraPosition, setCameraPosition] = useState<Vector3>();
  const [target, setTarget] = useState<Vector3>();

  return (
    <EditorContext.Provider
      value={{
        configurator,
        model,
        hoveredListItemIndex,
        setHoveredListItemIndex,
        cameraPosition,
        setCameraPosition,
        selection,
        setSelection,
      }}
    >
      <div className="flex h-full">
        <div className="flex-grow w-0 relative">
          <Model
            ref={canvasRef}
            onSelect={(index) => setSelection(index == null ? undefined : { index, type: "part" })}
            highlightIndex={selection?.type === "part" ? selection.index : hoveredListItemIndex}
            onLoad={setModel}
            url={configurator.model_url}
            onCameraPosition={setCameraPosition}
            onTarget={setTarget}
            initialCameraPosition={configurator.options.initialCameraPosition}
            initialTarget={configurator.options.initialTarget}
            rotation={configurator.options.rotation}
          />
          <TakePhotoButton canvasRef={canvasRef} />
        </div>
        {!model?.scene ? null : (
          <div className="w-[458px] flex-shrink-0 border-l">
            {selection?.type === "part" ? (
              <ModelPartCustomizer
                onClose={() => setSelection(undefined)}
                key={selection.index}
                index={selection.index}
                initialValue={configurator.options.customizers.find(
                  (x) => x.targetIndexes.includes(selection.index) && x.targetIndexes.length === 1
                )}
                mesh={Object.values(model.nodes)[selection.index]}
                onSave={(customizer) => {
                  let allCustomizers = [...configurator.options.customizers];
                  const index = allCustomizers.findIndex((x) => x.targetIndexes.includes(selection.index));
                  if (index === -1) {
                    allCustomizers.push(customizer);
                  } else {
                    allCustomizers[index] = customizer;
                  }
                  setConfigurator((prev) => {
                    if (!prev) return prev;
                    return { ...prev, options: { ...prev.options, customizers: allCustomizers } };
                  });
                  setSelection(undefined);
                }}
              />
            ) : selection?.type === "group" ? (
              <GroupCustomizer
                onClose={() => setSelection(undefined)}
                model={model}
                initialValue={configurator.options.groups[selection.index]}
                customizers={configurator.options.customizers}
                onSave={(val) => {
                  let allGroups = [...configurator.options.groups];
                  allGroups[selection.index] = val;
                  setConfigurator((prev) => {
                    if (!prev) return prev;
                    return { ...prev, options: { ...prev.options, groups: allGroups } };
                  });
                  setSelection(undefined);
                }}
                onDelete={() => {
                  let allGroups = [...configurator.options.groups];
                  allGroups.splice(selection.index, 1);
                  setConfigurator((prev) => {
                    if (!prev) return prev;
                    return { ...prev, options: { ...prev.options, groups: allGroups } };
                  });
                  setSelection(undefined);
                }}
              />
            ) : (
              <div className="overflow-auto h-full">
                <div className="space-y-6 p-6 border-b">
                  <div className="">
                    <div className="mb-3 font-bold text-lg">Product</div>
                    <div className="flex mb-2">
                      <div className="w-[100px]">Title</div>
                      <div className="flex-grow">
                        <Input
                          className="w-full"
                          value={configurator.product_title}
                          onChange={(e) => setConfigurator((prev) => (!prev ? prev : { ...prev, product_title: e.target.value }))}
                        />
                      </div>
                    </div>
                    <div className="flex">
                      <div className="w-[100px]">Base Price</div>
                      <div className="flex-grow">
                        <Input
                          className="w-full"
                          value={configurator.base_price}
                          onChange={(e) => setConfigurator((prev) => (!prev ? prev : { ...prev, base_price: +e.target.value }))}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <ModelPartsList onSelect={(index) => setSelection({ type: "part", index })} />
                <GroupsList
                  onAdd={() => {
                    setConfigurator((prev) => {
                      if (!prev) return prev;
                      return {
                        ...prev,
                        options: {
                          ...prev.options,
                          groups: [
                            ...prev.options.groups,
                            {
                              id: uniqid(),
                              name: `Group ${prev.options.groups.length + 1}`,
                              targetIndexes: [],
                              color: { label: "", options: [], targetIndex: -1, type: "color" },
                              material: { label: "", options: [], targetIndex: -1, type: "material" },
                            },
                          ],
                        },
                      };
                    });
                  }}
                  groups={configurator.options.groups}
                  model={model}
                  onSelect={(index) => setSelection({ type: "group", index })}
                />
                <CameraPositionField
                  setPosition={() => {
                    setConfigurator((prev) =>
                      !prev
                        ? prev
                        : {
                            ...prev,
                            options: {
                              ...prev.options,
                              initialCameraPosition: cameraPosition?.toArray(),
                              initialTarget: target?.toArray(),
                            },
                          }
                    );
                  }}
                  position={{ camera: configurator.options.initialCameraPosition, target: configurator.options.initialTarget }}
                />
                <InitialSelectionsField configurator={configurator} setConfigurator={setConfigurator} />
                <RotationField configurator={configurator} setConfigurator={setConfigurator} />
              </div>
            )}
          </div>
        )}
      </div>
    </EditorContext.Provider>
  );
};

export default Editor;
